<script lang="ts">
import type { UploadScope as UploadScopeType } from '#layers/assets/stores/upload';
import type { MonospaceFile } from '#layers/data-model/public';

export interface UploadModalProps<C extends boolean = true> {
	/**
	 * Close the modal immediately when it is submitted, don't wait for the upload to finish
	 */
	closeOnSubmit?: C & boolean;
	/**
	 * Level at which files should be uploaded
	 * @default 'workspace'
	 */
	scope?: UploadScopeType;
}

export type UploadModalResult<C> = C extends true ? { files: Promise<MonospaceFile[]> } : { files: MonospaceFile[] };
</script>

<script setup lang="ts" generic="C extends boolean = true">
import { ref } from 'vue';
import { useFileUpload } from '#layers/assets/composables/use-file-upload';
import { useFileUploadLimit } from '#layers/assets/composables/use-file-upload-limit';
import { UploadScope } from '#layers/assets/stores/upload';
import { useToastResultHandling } from '#layers/ui/public';

const { closeOnSubmit = true as never, scope = UploadScope.Workspace } = defineProps<UploadModalProps<C>>();

const emit = defineEmits<{
	close: [result?: UploadModalResult<C>];
}>();

const selectedFiles = ref<File[]>([]);

const { resolveUploadErrorMessage } = useFileUploadLimit();
const { onError: showUploadError } = useToastResultHandling();

const { uploadFiles, isUploading } = useFileUpload(() => ({
	scope,
	onError: (error) => showUploadError(error, { description: resolveUploadErrorMessage(error) }),
}));

async function handleSubmit() {
	const uploadPromise = uploadFiles(selectedFiles.value);

	if (closeOnSubmit) {
		emit('close', { files: uploadPromise } as UploadModalResult<C>);
	}
	else {
		const files = await uploadPromise;
		emit('close', { files } as UploadModalResult<C>);
	}
}

function handleCancel() {
	emit('close');
}
</script>

<template>
	<DDialog :title="$t('upload.title')">
		<template #body>
			<FileUpload
				v-model="selectedFiles"
				multiple
				layout="list"
			/>
		</template>
		<template #footer>
			<DButton
				:label="$t('common.cancel')"
				color="neutral"
				variant="outline"
				size="sm"
				:disabled="isUploading"
				@click="handleCancel"
			/>
			<DButton
				:label="$t('upload.action')"
				size="sm"
				:disabled="selectedFiles.length === 0 || isUploading"
				:loading="isUploading"
				@click="handleSubmit"
			/>
		</template>
	</DDialog>
</template>
